import type { IAugmentedJQuery, IComponentController, IOnChangesObject } from 'angular';

import template from './mx-table.component.html';
import { ColumnAlignmentsEnum, ColumnDisplayImageEnum, DataQueryColumn } from '../../Shell/PandoraSolutionBuilderAgent/modules/DataQueryModel/contracts';
import { DataQueryHelperService } from '../../Shell/PandoraSolutionBuilderAgent/modules/DataQueryModel/data-query-helper.service';
import { MxObjectPropertyType } from '../../Shell/Pandora/model/object';
import type { PagingPreprocessor } from '../../Shell/PandoraSolutionBuilderAgent/modules/DataQueryModel/preprocessors/PagingPreprocessor';
import type { UserProfileService } from '../../App/services/user-profile-service';
import type { I18n } from '../../i18n/mx-internationalization';

export interface TableCell {
	Value: string;
	Link?: {
		Type?: string;
		ObjectId?: string;
		EntityType?: string;
		EditObject?: string;
		NavigationId?: string;
		Icon?: string;
		DashboardId?: string;
		DashboardKeyword?: string;
		InteractiveFilters?: Record<string, unknown>;
	};
}

export interface TableRow {
	cells: Record<string, TableCell>;
}

export interface UiTableRow {
	uiCells: GridCell[];
	trackById: number;
}

export interface GridCell {
	classes: string[];
	text: string;
	link?: TableCell['Link'] & { DashboardInteractiveFilters?: string };
	trackById: number;
	width?: string;
}

export class TableController implements IComponentController {
	private externalRows: TableRow[] = [];
	private rows: UiTableRow[] = [];
	private footer?: TableRow;
	private columns: DataQueryColumn[] = [];
	public headerCells: GridCell[] = [];
	public footerCells: GridCell[] = [];
	private pagingPreprocessor: PagingPreprocessor;
	private pageSize?: number;
	private pageNumber?: number;
	private totalItemsCount?: number;
	private resizeObserver?: ResizeObserver;
	public maxRows?: number;
	private initialized = false;

	static readonly $inject = [
		'$element',
		'mx.sb.Agent.DataQuery.DataQueryHelperService',
		'mx.sb.Agent.DataQuery.PagingPreprocessor',
		'mx.shell.UserProfileService',
		'mx.internationalization',
	];

	constructor(
		private readonly $element: IAugmentedJQuery,
		private readonly dataQueryHelperService: DataQueryHelperService,
		pagingPreprocessorFactory: () => PagingPreprocessor,
		private readonly userProfileService: UserProfileService,
		private readonly i18n: I18n
	) {
		this.pagingPreprocessor = pagingPreprocessorFactory();
		this.pagingPreprocessor.addChangedHandler(() => {
			this.pageNumber = this.pagingPreprocessor.page;
		});
	}

	$onInit() {
		this.pagingPreprocessor.init(
			{},
			{
				pageSize: this.pageSize,
				pageSizes: this.pageSize ? [this.pageSize] : undefined,
				totalItems: this.totalItemsCount,
				page: this.pageNumber,
			}
		);

		this.initialized = this.initialized || this.isExportMode();
	}

	$onChanges(onChangesObj: IOnChangesObject) {
		if (onChangesObj.externalRows || onChangesObj.columns || onChangesObj.footer) {
			this.prepareData();
		}

		if (onChangesObj.totalItemsCount && typeof onChangesObj.totalItemsCount.currentValue === 'number') {
			this.pagingPreprocessor.totalItems = onChangesObj.totalItemsCount.currentValue;
		}

		if (onChangesObj.pageSize && typeof onChangesObj.pageSize.currentValue === 'number') {
			const page = this.pageNumber ?? 0;
			this.pagingPreprocessor.pageSize = onChangesObj.pageSize.currentValue;
			this.pagingPreprocessor.page = page;
			this.initialized = onChangesObj.pageSize.currentValue > 0;

			if (!this.isExportMode()) {
				this.initResizeObserver();
			}
		}
	}

	$onDestroy() {
		this.resizeObserver?.disconnect();
	}

	private initResizeObserver() {
		if (!this.resizeObserver) {
			this.resizeObserver = new ResizeObserver(
				$.debounce(100, () => {
					const rowHeight = 32; //should be equal to styles
					const headerHeight = 48; //should be equal to styles
					const footerHeight = this.footerCells.length > 0 ? 32 : 0; //should be equal to styles
					const bottomMarginAndPaging = 2 + 20;
					const step = 5;
					const innerTable = this.$element.find('table');

					// current Table-height should not influence on the maxRows calculation, that's why we hide it for a moment before measuring the height
					innerTable.hide();
					const maxRows = Math.max(step, Math.floor((this.$element[0].clientHeight - headerHeight - footerHeight - bottomMarginAndPaging) / rowHeight / step) * step);
					const changed = this.maxRows !== maxRows;
					this.maxRows = maxRows;
					if (changed) {
						this.prepareData();
					}
					innerTable.show();
				})
			);
			this.resizeObserver.observe(this.$element[0]);
		}
	}

	private getCellValue(row: TableRow, column: DataQueryColumn) {
		return row?.cells?.[column.Name]?.Value;
	}

	private getAlignmentClass(column: DataQueryColumn) {
		switch (column.Alignment) {
			case ColumnAlignmentsEnum.Left:
				return 'mx-table--cell--left';
			case ColumnAlignmentsEnum.Center:
				return 'mx-table--cell--center';
			case ColumnAlignmentsEnum.Right:
				return 'mx-table--cell--right';
		}
	}

	private getCellClasses(column: DataQueryColumn) {
		return ['mx-table--cell', this.getAlignmentClass(column)];
	}

	private prepareData() {
		const columns = this.columns.toSorted((a, b) => a.Position - b.Position);
		const fullWidth = columns.reduce((acc, column) => acc + parseInt(column.Width || '50', 10), 0);

		const columnSizes = columns.reduce<Record<string, string>>((columnSizes, column) => {
			const width = parseInt(column.Width ?? '50', 10);
			columnSizes[column.Name] = isNaN(width) ? `${Math.floor(fullWidth / columns.length)}%` : `${Math.floor((width * 100) / fullWidth)}%`;
			return columnSizes;
		}, {});

		this.headerCells = columns.map((column) => ({
			text: column.Title,
			id: `column-${column.Name}`,
			classes: ['mx-table--cell', 'mx-table--cell-header', this.getAlignmentClass(column)],
			trackById: Math.random(),
			width: columnSizes[column.Name],
		}));

		const getCell = (row: TableRow, column: DataQueryColumn) => ({
			text: column.DisplayImage !== ColumnDisplayImageEnum.Image ? this.formatValue(this.getCellValue(row, column), column) : '',
			icon: this.getCellImage(row, column),
			link: this.prepareLink(row.cells[column.Name]?.Link),
			classes: this.getCellClasses(column),
			trackById: Math.random(),
			width: columnSizes[column.Name],
		});

		const getRow = (externalRow: TableRow) => ({
			trackById: Math.random(),
			uiCells: columns.map((column) => getCell(externalRow, column)),
		});

		this.rows = this.externalRows.map<UiTableRow>((externalRow) => getRow(externalRow));

		if (!this.isExportMode() && typeof this.maxRows === 'number' && this.maxRows > 0 && this.initialized) {
			if (this.rows.length > this.maxRows) {
				this.rows = this.rows.slice(0, this.maxRows);
			}

			if (this.rows.length < this.pagingPreprocessor.pageSize) {
				const emptyRows = this.pagingPreprocessor.pageSize - this.rows.length;
				for (let i = 0; i < emptyRows; i++) {
					this.rows.push(getRow({ cells: {} }));
				}
			}
		}

		if (this.footer) {
			const footerRow = this.footer;
			this.footerCells = columns.map((column) => {
				let cellValue: unknown = this.getCellValue(footerRow, column);
				if (column.AttributeValueType === MxObjectPropertyType.Currency.name && typeof cellValue === 'number') {
					cellValue = { Value: cellValue, CC: this.userProfileService.userInfo?.currency };
				}
				return {
					text: this.formatValue(cellValue, column),
					id: `footer-${column.Name}`,
					classes: this.getCellClasses(column).concat(['mx-table--cell-footer', this.getAlignmentClass(column)]),
					trackById: Math.random(),
					width: columnSizes[column.Name],
				};
			});
		} else {
			this.footerCells = [];
		}
	}

	private getCellImage(row: TableRow, column: DataQueryColumn) {
		if (column.ImageScript && column.DisplayImage !== ColumnDisplayImageEnum.None) {
			try {
				const func = new Function('$item', '$value', column.ImageScript);
				return func(row, this.getCellValue(row, column));
			} catch (e) {
				console.error(`Error executing Image script for column${column.Title ?? column.Name}: ${e instanceof Error ? e.message : e}`);
			}
		}

		return null;
	}

	private isExportMode() {
		//TODO: better to add bindable property from the designer instead
		return this.pagingPreprocessor.pageSize === 1000;
	}

	private formatValue(value: unknown, column: DataQueryColumn) {
		switch (column.AttributeValueType) {
			case MxObjectPropertyType.String.name:
			case MxObjectPropertyType.Guid.name:
			case MxObjectPropertyType.Text.name:
				return value;
			case MxObjectPropertyType.Boolean.name:
				return value === null || value === undefined || typeof value === 'string' ? value : this.i18n.get(`solutionBuilder.common.${value ? 'yes' : 'no'}`);
			default:
				return this.dataQueryHelperService.formatCellValue(value, column);
		}
	}

	private prepareLink(link: TableCell['Link']) {
		if (!link) {
			return;
		}
		const interactiveFilters = link.InteractiveFilters ? JSON.stringify(link.InteractiveFilters) : undefined;
		return {
			...link,
			DashboardInteractiveFilters: interactiveFilters,
		};
	}
}

export const TableComponent = {
	name: 'mxTable',
	controller: TableController,
	controllerAs: 'vm',
	template,
	bindings: {
		externalRows: '<rows',
		columns: '<',
		footer: '<',
		allowPaging: '<',
		pageSize: '<',
		pageNumber: '=?',
		totalItemsCount: '<totalItems',
		maxRows: '=?',
	},
};
